/**
 * ScopeHierarchyPage — Full-page scope DAG visualization.
 *
 * Wraps ScopeDAGNavigator + ScopeBreadcrumbNavigator from @ferp/workflow-forms.
 * Shows the org hierarchy (Contains), staff assignments (AssignedTo), and
 * location bindings (LocatedAt) as an interactive Cytoscape.js DAG.
 *
 * Route: /architect/scope-hierarchy
 * Access: Architect layer (RequireArchitect guard in App.tsx)
 */

import { useState, useCallback } from 'react';
import { Alert, Container, Loader, Stack, Title, Text } from '@mantine/core';
import { IconAlertCircle, IconHierarchy2 } from '@tabler/icons-react';
import {
  ScopeDAGNavigator,
  ScopeBreadcrumbNavigator,
  useScopeGraph,
} from '@ferp/workflow-forms/scope';

export function ScopeHierarchyPage() {
  const { graph, isLoading, error, scopePath, fetchScopePath } = useScopeGraph();

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const handleNodeSelect = useCallback(
    (actorId: string) => {
      setSelectedNodeId(actorId);
      fetchScopePath(actorId);
    },
    [fetchScopePath],
  );

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Scope Graph Error">
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container size="lg" py="xl">
        <Loader />
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <div>
          <Title order={2}>
            <IconHierarchy2 size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Scope Hierarchy
          </Title>
          <Text size="sm" c="dimmed">
            Organization structure, staff assignments, and location bindings (DRR-075)
          </Text>
        </div>

        <ScopeBreadcrumbNavigator
          selectedNodeId={selectedNodeId}
          onNodeNavigate={handleNodeSelect}
        />

        <ScopeDAGNavigator
          graph={graph}
          onNodeSelect={handleNodeSelect}
          selectedNodeId={selectedNodeId}
          scopePath={scopePath?.path}
          height={650}
        />
      </Stack>
    </Container>
  );
}
